"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const lodash_isequal_1 = __importDefault(require("lodash.isequal"));
const usa_combo_box_1 = __importDefault(require("@uswds/uswds/js/usa-combo-box"));
const uswds_react_utils_1 = require("@boozallen-uip/uswds-react-utils");
const __1 = require("..");
const ComboBox = (0, react_1.forwardRef)(({ children, className, comparator, disabled = false, error, filter, id, onChange, options, optionRenderer, placeholder, value, ...props }, ref) => {
    const containerRef = (0, react_1.useRef)(null);
    const comboBoxRef = (0, react_1.useRef)(null);
    const selectRef = (0, react_1.useRef)(null);
    (0, react_1.useImperativeHandle)(ref, () => selectRef.current);
    const valueRef = (0, react_1.useRef)(value);
    const pristineClass = 'usa-combo-box--pristine';
    const selectionIndex = (newValue) => {
        const index = options.findIndex((option) => comparator
            ? comparator(option, newValue)
            : (0, lodash_isequal_1.default)(option, newValue));
        return index >= 0 ? index : undefined;
    };
    // initialization
    (0, react_1.useEffect)(() => {
        const containerEl = containerRef.current;
        const selectEl = selectRef.current;
        if (!containerEl || !selectEl)
            return;
        // call USWDS's `on` function to initialize the component
        usa_combo_box_1.default.on(containerEl);
        // hook into the select's `change` event to monitor changes
        selectEl.addEventListener('change', (e) => {
            if (!onChange)
                return;
            // do not trigger onChange if the value did not change
            if (selectionIndex(valueRef.current) ===
                Number.parseInt(e.detail.value, 10)) {
                return;
            }
            const newValue = options[Number.parseInt(e.detail.value, 10)];
            valueRef.current = newValue;
            const event = new CustomEvent('change', {
                detail: {
                    name: id,
                    value: newValue
                }
            });
            onChange(event);
        });
        // on dismount, call USWDS's `off` function to cleanup the component
        return () => {
            usa_combo_box_1.default.off(containerEl);
        };
    }, []);
    // update the combo box's value whenever the value changes outside
    // of this component
    (0, react_1.useEffect)(() => {
        const comboBoxEl = comboBoxRef.current;
        const selectEl = selectRef.current;
        if (!comboBoxEl || !selectEl)
            return;
        const inputEl = comboBoxEl.querySelector('input');
        valueRef.current = value;
        const optionIndex = selectionIndex(value);
        let valueText = '';
        if (optionIndex !== undefined) {
            const valueOption = selectEl.querySelector(`option[value="${optionIndex}"]`);
            valueText = valueOption.innerHTML;
        }
        if (valueText !== inputEl.value) {
            inputEl.value = valueText;
            if (value) {
                selectEl.value = optionIndex?.toString() || '';
                comboBoxEl.classList.add(pristineClass);
            }
            else {
                // clear the combo box
                selectEl.value = '';
                comboBoxEl.classList.remove(pristineClass);
            }
        }
    }, [value, comboBoxRef, selectRef]);
    // call USWDS's `enable` or `disable` function when the disabled prop changes
    (0, react_1.useEffect)(() => {
        const comboBoxEl = comboBoxRef.current;
        if (!comboBoxEl)
            return;
        const disabledState = comboBoxEl.querySelector(`#${id}`).disabled;
        if (disabled === disabledState)
            return; // the input already has the right disabled state
        if (disabled)
            usa_combo_box_1.default.disable(comboBoxEl);
        else
            usa_combo_box_1.default.enable(comboBoxEl);
    }, [disabled]);
    return ((0, jsx_runtime_1.jsxs)("div", { className: "bah-uswds-combo-box", ref: containerRef, children: [(0, jsx_runtime_1.jsx)(__1.Label, { error: error, target: id, children: children }), (0, jsx_runtime_1.jsx)(__1.Form.Error, { error: error }), (0, jsx_runtime_1.jsx)("div", { className: "usa-combo-box", "data-default-value": selectionIndex(value), "data-filter": filter, "data-placeholder": placeholder, ref: comboBoxRef, children: (0, jsx_runtime_1.jsx)("select", { className: (0, uswds_react_utils_1.createClassString)('usa-select usa-combo-box__select', className), disabled: disabled, id: id, name: id, ref: selectRef, ...props, children: options.map((option, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    (0, jsx_runtime_1.jsx)("option", { value: index, children: optionRenderer
                            ? optionRenderer(option)
                            : option }, `${id}--${index}`))) }) })] }));
});
exports.default = ComboBox;
